import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

import { authCookie } from "helper/cookie";

import { LogType } from "./singleton/const";
import { log, requestLog } from "./singleton/logConfig";

import { loginUrl, refreshTokenUrl } from "./service/auth/urls";
import type { RefreshTokenRes } from "./service/auth/resTypes";

import {
  DEFAULT_VERIFICATION_SCOPE,
  type AxiosRequestConfigWithRetry,
  type FailedRequestQueueItem,
  type HttpClientDependencies,
  type HttpClientRequestConfig,
  type LogoutPayload,
} from "./types";

const HEADER_SECTION = "erp2fa";

/**
 * Giữ cờ verify trên config sau khi merge `headers` — đảm bảo `requiresVerification`
 * không bị mất (một số path merge Axios/AxiosHeaders).
 */
function copyVerificationMetadata(
  source: AxiosRequestConfigWithRetry,
  target: AxiosRequestConfigWithRetry
): void {
  if (source.requiresVerification === true) {
    target.requiresVerification = true;
  }
  if (
    source.verificationScope !== undefined &&
    source.verificationScope !== ""
  ) {
    target.verificationScope = source.verificationScope;
  }
  if (source._retry === true) target._retry = true;
  if (source._verifyRetry === true) target._verifyRetry = true;
}

/**
 * Client axios **không** gắn interceptor — chỉ dùng cho POST refresh token,
 * tránh vòng lặp interceptor khi access token hết hạn.
 */
function createRefreshTokenClient(baseURL: string): AxiosInstance {
  return axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
    transformRequest: [(data) => JSON.stringify(data)],
  });
}

function unixSecondsFromNow(secondsFromNow: number): string {
  return String(Math.floor(Date.now() / 1000) + Math.floor(secondsFromNow));
}

function applyBearerToken(
  config: AxiosRequestConfigWithRetry,
  accessToken: string
): AxiosRequestConfigWithRetry {
  const headers = AxiosHeaders.from(config.headers ?? {});
  headers.set("Authorization", `Bearer ${accessToken}`);
  return {
    ...config,
    headers,
  };
}

function isErrOtpResponseData(data: unknown): boolean {
  if (data !== null && typeof data === "object" && "code" in data) {
    return (data as { code?: string }).code === "ERR_OTP";
  }
  return false;
}

abstract class HttpClient {
  protected readonly instance: AxiosInstance;

  private readonly refreshClient: AxiosInstance;

  private readonly deps: HttpClientDependencies;

  /** Section token theo scope — memory only, không cookie/localStorage */
  private readonly sectionTokens = new Map<string, string>();

  /**
   * Một Promise đang chờ OTP cho scope — đồng bộ mọi caller cùng scope
   * (thay hàng đợi thủ công; luôn clear sau settle trong executeOtpFlowForScope).
   */
  private readonly pendingOtpByScope = new Map<string, Promise<string>>();

  /** AbortController đang mở cho luồng OTP theo scope — timeout / clear / logout */
  private readonly otpAbortControllers = new Map<string, AbortController>();

  protected isRefreshing = false;

  protected failedQueue: FailedRequestQueueItem[] = [];

  protected requestStartTime = Date.now();

  private _initializeResponseInterceptor = (): void => {
    this.instance.interceptors.request.use(
      (config) =>
        this._handleRequest(
          config as AxiosRequestConfigWithRetry
        ) as Promise<AxiosRequestConfigWithRetry>,
      this._handleRequestError
    );

    this.instance.interceptors.response.use(
      this._handleResponse,
      this._handleResponseError
    );
  };

  public constructor(
    baseURL: string,
    dependencies: HttpClientDependencies = {}
  ) {
    this.instance = axios.create({ baseURL });
    this.refreshClient = createRefreshTokenClient(baseURL);
    this.deps = dependencies;
    this._initializeResponseInterceptor();
  }

  /**
   * Xóa section token đã cache (gắn header `erp2fa` / `HEADER_SECTION`) — gọi khi cần buộc xác thực lại
   * (đóng modal OTP, đổi ngữ cảnh, session hết hiệu lực, v.v.).
   *
   * - **Không truyền `scope`**: xóa mọi token section, hủy mọi `AbortController` OTP đang mở.
   * - **`scope` cụ thể**: xóa token & hủy luồng OTP chờ cho scope đó; map dedupe OTP được dọn trong
   *   `finally` sau khi promise từ `abort` kết thúc (tránh tạo hai luồng OTP song song).
   *
   * Gọi trên singleton HTTP, ví dụ `import baseHttp from "core/http/singleton/base"` rồi
   * `baseHttp.clearVerificationSection()` hoặc `baseHttp.clearVerificationSection(DEFAULT_VERIFICATION_SCOPE)`.
   */
  public clearVerificationSection(scope?: string): void {
    if (scope === undefined) {
      this.sectionTokens.clear();
      this.abortAllOtpControllers();
      return;
    }
    this.sectionTokens.delete(scope);
    const c = this.otpAbortControllers.get(scope);
    if (c) {
      c.abort();
      this.otpAbortControllers.delete(scope);
    }
  }

  private abortAllOtpControllers(): void {
    this.otpAbortControllers.forEach((c) => {
      c.abort();
    });
    this.otpAbortControllers.clear();
  }

  private flushQueue = (error: unknown, token: string | null = null): void => {
    const queue = this.failedQueue;
    this.failedQueue = [];
    queue.forEach((item) => {
      if (error) {
        item.reject(error);
      } else if (token) {
        item.resolve(token);
      }
    });
  };

  private triggerLogout(payload?: LogoutPayload): void {
    this.sectionTokens.clear();
    this.abortAllOtpControllers();
    this.deps.onLogout?.(payload);
  }

  private persistTokensFromRefresh(data: RefreshTokenRes): void {
    authCookie.setAccessToken(data.access_token ?? "");
    authCookie.setRefreshToken(data.refresh_token ?? "");
    authCookie.setAccessTokenExpiredTime(
      unixSecondsFromNow(data.expires_in ?? 0)
    );
    authCookie.setRefreshTokenExpiredTime(
      unixSecondsFromNow(data.refresh_expires_in ?? 0)
    );
  }

  /**
   * Type guard: chỉ `requiresVerification === true` mới bật luồng OTP.
   */
  private requiresVerificationActive(
    config: AxiosRequestConfigWithRetry
  ): config is AxiosRequestConfigWithRetry & { requiresVerification: true } {
    return config.requiresVerification === true;
  }

  private getVerificationScope(config: AxiosRequestConfigWithRetry): string {
    const scope = config.verificationScope;
    if (scope === undefined || scope === "") {
      return DEFAULT_VERIFICATION_SCOPE;
    }
    return scope;
  }

  /**
   * OTP + timeout: race giữa promise app và timeout; timeout → abort + reject.
   */
  private async runOtpWithTimeout(scope: string): Promise<string> {
    const onRequireOtp = this.deps.onRequireOtp;
    if (!onRequireOtp) {
      throw new Error(
        "HttpClient: requiresVerification but onRequireOtp is not configured"
      );
    }

    const ms = this.deps.otpFlowTimeoutMs ?? 60_000;
    const previous = this.otpAbortControllers.get(scope);
    previous?.abort();

    const controller = new AbortController();
    this.otpAbortControllers.set(scope, controller);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        reject(
          new Error(
            `HttpClient: OTP flow timed out after ${ms}ms (scope: ${scope})`
          )
        );
      }, ms);
    });

    try {
      const token = await Promise.race([
        onRequireOtp(controller.signal),
        timeoutPromise,
      ]);
      return token;
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      this.otpAbortControllers.delete(scope);
    }
  }

  /**
   * Gọi `onRequireOtp` một lần / scope; lưu token; luôn gỡ `pendingOtpByScope` sau settle.
   */
  private async executeOtpFlowForScope(scope: string): Promise<string> {
    try {
      const token = await this.runOtpWithTimeout(scope);
      this.sectionTokens.set(scope, token);
      return token;
    } catch (err: unknown) {
      this.sectionTokens.delete(scope);
      throw err;
    } finally {
      this.pendingOtpByScope.delete(scope);
    }
  }

  /**
   * Dedupe theo scope: cùng scope share một Promise; token cache trong Map.
   */
  private async acquireSectionToken(scope: string): Promise<string> {
    const cached = this.sectionTokens.get(scope);
    if (cached !== undefined) {
      return cached;
    }

    if (!this.pendingOtpByScope.has(scope)) {
      this.pendingOtpByScope.set(scope, this.executeOtpFlowForScope(scope));
    }

    const pending = this.pendingOtpByScope.get(scope);
    if (pending === undefined) {
      throw new Error("HttpClient: unexpected empty pending OTP promise");
    }
    return pending;
  }

  private async performTokenRefresh(
    refreshToken: string
  ): Promise<RefreshTokenRes> {
    const { data } = await this.refreshClient.post<RefreshTokenRes>(
      refreshTokenUrl,
      { refresh_token: refreshToken }
    );

    if (!data?.access_token) {
      throw new Error("Refresh token response missing access_token");
    }

    return data;
  }

  private _handleResponse = (response: AxiosResponse): AxiosResponse => {
    return response;
  };

  /**
   * 403 ERR_OTP: xóa token scope, gọi lại luồng OTP rõ ràng (không chỉ dựa interceptor),
   * gắn `X-Section`, `_verifyRetry` chặn vòng lặp.
   */
  private async retryRequestAfterErrOtp(
    originalRequest: AxiosRequestConfigWithRetry
  ): Promise<AxiosResponse> {
    originalRequest._verifyRetry = true;
    const scope = this.getVerificationScope(originalRequest);
    this.sectionTokens.delete(scope);

    const section = await this.acquireSectionToken(scope);

    const headers = AxiosHeaders.from(originalRequest.headers ?? {});
    headers.set(HEADER_SECTION, section);

    return this.instance.request({
      ...originalRequest,
      headers,
    });
  }

  private shouldRetryAfterErrOtp(
    axiosError: AxiosError,
    originalRequest: AxiosRequestConfigWithRetry
  ): boolean {
    return (
      axiosError.response?.status === 403 &&
      isErrOtpResponseData(axiosError.response?.data) &&
      this.requiresVerificationActive(originalRequest) &&
      originalRequest._verifyRetry !== true
    );
  }

  private async handleRefresh401(
    originalRequest: AxiosRequestConfigWithRetry,
    refreshToken: string
  ): Promise<AxiosResponse> {
    if (this.isRefreshing) {
      const newAccessToken = await new Promise<string>((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
      const next = applyBearerToken(originalRequest, newAccessToken);
      return this.instance.request(next);
    }

    originalRequest._retry = true;
    this.isRefreshing = true;
    try {
      const data = await this.performTokenRefresh(refreshToken);
      this.persistTokensFromRefresh(data);
      const accessToken = data.access_token!;
      this.flushQueue(null, accessToken);
      const next = applyBearerToken(originalRequest, accessToken);
      return await this.instance.request(next);
    } catch (err: unknown) {
      this.triggerLogout({
        toastMsg: "Hết phiên đăng nhập, vui lòng đăng nhập lại.",
      });
      this.flushQueue(err, null);
      throw err;
    } finally {
      this.isRefreshing = false;
    }
  }

  private _handleResponseError = async (error: unknown): Promise<unknown> => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const axiosError = error as AxiosError;
    const rawConfig = axiosError.config as
      | AxiosRequestConfigWithRetry
      | undefined;

    const method = rawConfig?.method;
    const url = rawConfig?.url;
    const baseURL = rawConfig?.baseURL ?? "";
    requestLog(method, url, error, LogType.RESPONSE, baseURL);

    if (!rawConfig) {
      return Promise.reject(error);
    }

    const originalRequest = rawConfig;

    if (this.shouldRetryAfterErrOtp(axiosError, originalRequest)) {
      try {
        return await this.retryRequestAfterErrOtp(originalRequest);
      } catch (retryErr: unknown) {
        return Promise.reject(retryErr);
      }
    }

    const refreshToken = authCookie.getRefreshToken();

    if (!refreshToken) {
      this.isRefreshing = false;
      originalRequest._retry = false;

      if (
        originalRequest.url !== loginUrl &&
        originalRequest.url !== refreshTokenUrl
      ) {
        this.triggerLogout();
      }
      return Promise.reject(error);
    }

    if (
      axiosError.response?.status === 401 &&
      originalRequest._retry !== true
    ) {
      try {
        return await this.handleRefresh401(originalRequest, refreshToken);
      } catch (e: unknown) {
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  };

  private _handleRequestError = (error: unknown): Promise<never> => {
    if (axios.isAxiosError(error)) {
      log("request.error", error.response?.data);
    } else {
      log("request.error", error);
    }
    return Promise.reject(error);
  };

  private async _handleRequest(
    req: AxiosRequestConfigWithRetry
  ): Promise<AxiosRequestConfigWithRetry> {
    const accessToken = authCookie.getAccessToken();
    this.requestStartTime = Date.now();

    const headers = AxiosHeaders.from(req.headers ?? {});

    if (accessToken && req.url !== loginUrl) {
      const ct =
        headers.get("Content-Type") ??
        headers.get("content-type") ??
        "application/json";
      headers.set("Content-Type", String(ct));
      headers.set("Authorization", `Bearer ${accessToken}`);
    } else {
      headers.set("Content-Type", "application/json");
      headers.delete("Authorization");
    }

    let next: AxiosRequestConfigWithRetry = {
      ...req,
      headers,
    };
    copyVerificationMetadata(req, next);

    // requiresVerification === true → chờ OTP, lấy section token, gắn header X-Section
    if (this.requiresVerificationActive(next)) {
      const scope = this.getVerificationScope(next);
      const section = await this.acquireSectionToken(scope);
      const h = AxiosHeaders.from(next.headers ?? {});
      h.set(HEADER_SECTION, section);
      next = { ...next, headers: h };
    }

    const { method, url, baseURL } = next;
    requestLog(method, url, next, LogType.REQUEST, baseURL || "");

    return next;
  }

  public get = <T>(
    url: string,
    params: NonNullable<AxiosRequestConfig["params"]> = {},
    config: HttpClientRequestConfig = {}
  ): AxiosPromise<T> =>
    this.instance.get<T>(url, {
      params,
      ...config,
    });

  public post = <T>(
    url: string,
    data: unknown = {},
    config: HttpClientRequestConfig = {}
  ): AxiosPromise<T> => this.instance.post<T>(url, data, { ...config });

  public put = <T>(
    url: string,
    data: unknown = {},
    config: HttpClientRequestConfig = {}
  ): AxiosPromise<T> => this.instance.put<T>(url, data, { ...config });

  public patch = <T>(
    url: string,
    data: unknown = {},
    config: HttpClientRequestConfig = {}
  ): AxiosPromise<T> => this.instance.patch<T>(url, data, { ...config });

  public delete = <T>(
    url: string,
    config: HttpClientRequestConfig = {}
  ): AxiosPromise<T> => this.instance.delete<T>(url, { ...config });
}

export default HttpClient;

export type {
  HttpClientDependencies,
  LogoutPayload,
  AxiosRequestConfigWithRetry,
  HttpClientRequestConfig,
  HttpClientRequestFlags,
} from "./types";

export { DEFAULT_VERIFICATION_SCOPE } from "./types";
